/**
 * CR075: Unified Flyer Extraction Engine
 * 
 * Single source of truth for flyer processing used by:
 * - Flyer Diagnostics v2 (tuning lab)
 * - Batch Run S1 Flyer processing
 * 
 * Stages:
 * 1. OCR - Text + layout extraction per page
 * 2. Candidate generation - Apply tunable signals, return scored candidates
 * 3. Deep scan - Run AI extraction on candidate regions
 */

import { supabase } from '@/integrations/supabase/client';
import { computeCandidateScore, computePrimaryOcrScore, DEFAULT_CANDIDATE_SCORING_CONFIG } from './candidateScoringEngine';
import type {
  CandidateScoringConfig,
  PrimaryOcrEngine,
  PrimaryOcrResult,
  CandidateScoringResult,
  AiScanResult,
  NormalizedDeal,
  AiDealCandidate,
} from './types';

// ============================================================================
// ENGINE CONFIG TYPES
// ============================================================================

export interface OcrSettings {
  engine: PrimaryOcrEngine;
  /** DPI for rendering (future) */
  dpi?: number;
  /** Multi-pass OCR mode (future) */
  passCount?: number;
}

export interface CandidateScoringSettings {
  /** Profile ID to load from DB, or use signals directly */
  profileId?: string;
  /** Direct signal config (overrides profileId) */
  signals?: CandidateScoringConfig['signals'];
  /** Score threshold for candidate selection */
  threshold: number;
  /** Minimum OCR quality score */
  minOcrScore: number;
}

export interface DeepScanPolicy {
  /** Whether AI scanning is enabled */
  enabled: boolean;
  /** Maximum candidates to scan per page */
  maxCandidatesPerPage: number;
  /** Maximum total AI calls per run */
  maxTotalAiCalls: number;
  /** Minimum candidate score to deep scan */
  minCandidateScore?: number;
}

export interface ExtractorSettings {
  /** AI model to use for extraction */
  model: string;
  /** Max tokens for AI response */
  maxTokens?: number;
  /** Temperature for AI */
  temperature?: number;
}

export interface ValidationSettings {
  /** Required fields for valid deals */
  requiredFields: string[];
  /** Minimum confidence threshold */
  minConfidence: number;
}

export interface DiagnosticsSettings {
  /** Store evidence crops/snippets */
  storeEvidence: boolean;
  /** Store raw OCR text */
  storeOcrText: boolean;
  /** Store AI raw output */
  storeAiRawOutput: boolean;
}

/**
 * Complete configuration snapshot for a flyer extraction run.
 * Both Diagnostics v2 and Batch Runs use this same config structure.
 */
export interface FlyerExtractionConfig {
  version: string;
  ocr: OcrSettings;
  candidateScoring: CandidateScoringSettings;
  deepScan: DeepScanPolicy;
  extractor: ExtractorSettings;
  validation: ValidationSettings;
  diagnostics: DiagnosticsSettings;
}

// ============================================================================
// ENGINE OUTPUT TYPES
// ============================================================================

export interface PageOcrResult {
  pageNumber: number;
  imageUrl: string;
  imageStoragePath?: string;
  ocr: PrimaryOcrResult;
}

export interface PageCandidateResult extends PageOcrResult {
  candidateScoring: CandidateScoringResult;
  isCandidate: boolean;
}

export interface PageDeepScanResult extends PageCandidateResult {
  aiScan: AiScanResult | null;
  deals: NormalizedDeal[];
}

export interface FlyerExtractionResult {
  runId: string;
  merchantName: string;
  status: 'complete' | 'partial' | 'failed';
  config: FlyerExtractionConfig;
  pages: PageDeepScanResult[];
  metrics: ExtractionMetrics;
  errors: ExtractionError[];
}

export interface ExtractionMetrics {
  pagesProcessed: number;
  pagesOcrCompleted: number;
  candidatesFound: number;
  candidatesScanned: number;
  dealsExtracted: number;
  dealsRejected: number;
  rejectionReasons: Record<string, number>;
  totalDurationMs: number;
  ocrDurationMs: number;
  scoringDurationMs: number;
  aiDurationMs: number;
}

export interface ExtractionError {
  stage: 'ocr' | 'scoring' | 'deepScan' | 'validation';
  pageNumber?: number;
  message: string;
  details?: Record<string, unknown>;
}

// ============================================================================
// DEFAULT CONFIG
// ============================================================================

export const DEFAULT_EXTRACTION_CONFIG: FlyerExtractionConfig = {
  version: '2026-01-18.1',
  ocr: {
    engine: 'tesseract',
    dpi: 150,
    passCount: 1,
  },
  candidateScoring: {
    threshold: 7,
    minOcrScore: 25,
  },
  deepScan: {
    enabled: true,
    maxCandidatesPerPage: 5,
    maxTotalAiCalls: 50,
    minCandidateScore: 7,
  },
  extractor: {
    model: 'google/gemini-3-flash-preview',
    maxTokens: 4096,
    temperature: 0.1,
  },
  validation: {
    requiredFields: ['deal_type'],
    minConfidence: 0.3,
  },
  diagnostics: {
    storeEvidence: true,
    storeOcrText: true,
    storeAiRawOutput: true,
  },
};

// ============================================================================
// OCR STAGE
// ============================================================================

async function runOcr(
  imageUrl: string,
  engine: PrimaryOcrEngine
): Promise<PrimaryOcrResult> {
  const startTime = Date.now();

  if (engine === 'none') {
    return {
      engine: 'none',
      text: '',
      confidence: 0,
      duration_ms: 0,
      score_total: 0,
      score_breakdown: {
        alphanumeric_ratio: 0,
        currency_pattern_count: 0,
        symbol_noise_ratio: 0,
        text_length: 0,
        word_count: 0,
      },
    };
  }

  try {
    const Tesseract = await import('tesseract.js');
    const worker = await Tesseract.createWorker('eng');

    console.log(`[FlyerEngine] Running Tesseract OCR on: ${imageUrl.substring(0, 50)}...`);

    const result = await worker.recognize(imageUrl);
    await worker.terminate();

    const text = result.data.text || '';
    const confidence = result.data.confidence || 0;
    const durationMs = Date.now() - startTime;

    const scoreResult = computePrimaryOcrScore(text);

    console.log(`[FlyerEngine] Tesseract complete: ${text.length} chars, confidence=${confidence}%, score=${scoreResult.total}`);

    return {
      engine: 'tesseract',
      text,
      confidence,
      duration_ms: durationMs,
      score_total: scoreResult.total,
      score_breakdown: {
        alphanumeric_ratio: scoreResult.breakdown.alphanumeric_ratio,
        currency_pattern_count: scoreResult.breakdown.currency_pattern_count,
        symbol_noise_ratio: scoreResult.breakdown.symbol_noise_ratio,
        text_length: scoreResult.breakdown.text_length,
        word_count: scoreResult.breakdown.word_count,
      },
    };
  } catch (error) {
    console.error(`[FlyerEngine] OCR error:`, error);
    return {
      engine,
      text: '',
      confidence: 0,
      duration_ms: Date.now() - startTime,
      score_total: 0,
      score_breakdown: {
        alphanumeric_ratio: 0,
        currency_pattern_count: 0,
        symbol_noise_ratio: 0,
        text_length: 0,
        word_count: 0,
      },
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// ============================================================================
// CANDIDATE SCORING STAGE
// ============================================================================

async function loadScoringConfig(
  settings: CandidateScoringSettings
): Promise<CandidateScoringConfig> {
  // Direct signals override DB lookup
  if (settings.signals) {
    return {
      version: 'custom',
      enabled: true,
      threshold: settings.threshold,
      min_primary_ocr_score: settings.minOcrScore,
      signals: settings.signals,
    };
  }

  // Load from DB by profile ID
  if (settings.profileId) {
    const { data, error } = await supabase
      .from('flyer_candidate_scoring_config')
      .select('*')
      .eq('id', settings.profileId)
      .eq('is_active', true)
      .single();

    if (!error && data) {
      return {
        version: data.version,
        enabled: data.enabled,
        threshold: settings.threshold,
        min_primary_ocr_score: settings.minOcrScore,
        signals: (Array.isArray(data.signals) ? data.signals : []) as unknown as CandidateScoringConfig['signals'],
      };
    }
  }

  // Fall back to active config or defaults
  const { data } = await supabase
    .from('flyer_candidate_scoring_config')
    .select('*')
    .eq('is_active', true)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (data) {
    return {
      version: data.version,
      enabled: data.enabled,
      threshold: settings.threshold,
      min_primary_ocr_score: settings.minOcrScore,
      signals: (Array.isArray(data.signals) ? data.signals : []) as unknown as CandidateScoringConfig['signals'],
    };
  }

  // Ultimate fallback
  return {
    ...DEFAULT_CANDIDATE_SCORING_CONFIG,
    threshold: settings.threshold,
    min_primary_ocr_score: settings.minOcrScore,
  };
}

function evaluateCandidate(
  ocrText: string,
  primaryOcrScore: number,
  config: CandidateScoringConfig
): CandidateScoringResult {
  return computeCandidateScore(ocrText, primaryOcrScore, config);
}

// ============================================================================
// DEEP SCAN STAGE
// ============================================================================

async function runDeepScan(
  imageUrl: string,
  pageNumber: number,
  merchantName: string,
  ocrContext: string,
  model: string
): Promise<AiScanResult> {
  const startTime = Date.now();

  try {
    console.log(`[FlyerEngine] Starting AI deep scan for page ${pageNumber} using ${model}`);

    const { data, error } = await supabase.functions.invoke('ai-page-scan', {
      body: {
        image_url: imageUrl,
        page_number: pageNumber,
        merchant_name: merchantName,
        ocr_context: ocrContext,
        model,
      },
    });

    if (error) {
      throw new Error(`AI scan function error: ${error.message}`);
    }

    const durationMs = Date.now() - startTime;

    if (!data.success) {
      return {
        status: 'failed',
        deals: [],
        raw_output: data,
        duration_ms: durationMs,
        model_used: data.model_used || model,
        error: data.error || 'AI scan failed',
      };
    }

    console.log(`[FlyerEngine] AI scan complete: ${data.deals?.length || 0} deals found`);

    return {
      status: 'complete',
      deals: data.deals || [],
      raw_output: data,
      duration_ms: durationMs,
      model_used: data.model_used || model,
    };
  } catch (error) {
    console.error(`[FlyerEngine] AI deep scan error:`, error);
    return {
      status: 'failed',
      deals: [],
      duration_ms: Date.now() - startTime,
      model_used: model,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// ============================================================================
// DEAL NORMALIZATION
// ============================================================================

function normalizeDeals(
  aiResult: AiScanResult,
  runId: string,
  pageNumber: number,
  primaryScore: number,
  candidateScore: number,
  candidateReasonCodes: string[],
  validation: ValidationSettings
): { deals: NormalizedDeal[]; rejected: number; rejectionReasons: Record<string, number> } {
  const deals: NormalizedDeal[] = [];
  let rejected = 0;
  const rejectionReasons: Record<string, number> = {};

  if (aiResult.status !== 'complete' || !aiResult.deals.length) {
    return { deals, rejected, rejectionReasons };
  }

  for (let i = 0; i < aiResult.deals.length; i++) {
    const deal = aiResult.deals[i];

    // Validate confidence
    if (deal.confidence < validation.minConfidence) {
      rejected++;
      const reason = 'LOW_CONFIDENCE';
      rejectionReasons[reason] = (rejectionReasons[reason] || 0) + 1;
      continue;
    }

    // Validate required fields
    const missingFields = validation.requiredFields.filter((field) => {
      const value = deal[field as keyof AiDealCandidate];
      return value === undefined || value === null || value === '';
    });

    if (missingFields.length > 0) {
      rejected++;
      const reason = `MISSING_${missingFields[0].toUpperCase()}`;
      rejectionReasons[reason] = (rejectionReasons[reason] || 0) + 1;
      continue;
    }

    deals.push({
      deal_id: `${runId}-${pageNumber}-${i}`,
      run_id: runId,
      page_no: pageNumber,
      status: deal.deal_type === 'UNCLASSIFIED' ? 'REVIEW' : 'NEW',
      deal_type: deal.deal_type,
      merchant: deal.merchant || null,
      purchase: {
        brand: deal.purchase_brand || null,
        amount: deal.purchase_amount || 0,
        currency: 'CAD',
      },
      reward: {
        type: deal.reward_type || null,
        merchant: deal.reward_merchant || null,
        amount: deal.reward_amount || 0,
        currency: 'CAD',
      },
      validity: {
        start_date: deal.validity_start_date || null,
        end_date: deal.validity_end_date || null,
        raw_text: deal.validity_raw_text || null,
      },
      confidence: deal.confidence || 0,
      debug: {
        primary_score_total: primaryScore,
        candidate_score: candidateScore,
        candidate_reason_codes: candidateReasonCodes,
        ai_notes: deal.notes || [],
      },
      override: {
        was_overridden: false,
        overridden_by: null,
        overridden_at: null,
        override_reason: null,
      },
    });
  }

  return { deals, rejected, rejectionReasons };
}

// ============================================================================
// MAIN ENGINE FUNCTION
// ============================================================================

export interface FlyerPageInput {
  pageNumber: number;
  imageUrl: string;
  imageStoragePath?: string;
}

export interface RunFlyerExtractionParams {
  runId: string;
  merchantName: string;
  pages: FlyerPageInput[];
  config: FlyerExtractionConfig;
  onProgress?: (phase: string, current: number, total: number) => void;
}

/**
 * Main entry point for flyer extraction.
 * Processes all pages through OCR → Candidate Scoring → Deep Scan pipeline.
 */
export async function runFlyerExtraction(
  params: RunFlyerExtractionParams
): Promise<FlyerExtractionResult> {
  const { runId, merchantName, pages, config, onProgress } = params;
  const startTime = Date.now();

  const results: PageDeepScanResult[] = [];
  const errors: ExtractionError[] = [];
  const metrics: ExtractionMetrics = {
    pagesProcessed: 0,
    pagesOcrCompleted: 0,
    candidatesFound: 0,
    candidatesScanned: 0,
    dealsExtracted: 0,
    dealsRejected: 0,
    rejectionReasons: {},
    totalDurationMs: 0,
    ocrDurationMs: 0,
    scoringDurationMs: 0,
    aiDurationMs: 0,
  };

  let aiCallsUsed = 0;

  // Load candidate scoring config once
  const scoringConfig = await loadScoringConfig(config.candidateScoring);

  for (const page of pages) {
    const { pageNumber, imageUrl, imageStoragePath } = page;

    // Stage 1: OCR
    onProgress?.('OCR', pageNumber, pages.length);
    const ocrStart = Date.now();
    const ocr = await runOcr(imageUrl, config.ocr.engine);
    metrics.ocrDurationMs += Date.now() - ocrStart;

    if (ocr.error) {
      errors.push({
        stage: 'ocr',
        pageNumber,
        message: ocr.error,
      });
    } else {
      metrics.pagesOcrCompleted++;
    }

    // Stage 2: Candidate Scoring
    onProgress?.('Scoring', pageNumber, pages.length);
    const scoringStart = Date.now();
    const candidateScoring = evaluateCandidate(ocr.text, ocr.score_total, scoringConfig);
    metrics.scoringDurationMs += Date.now() - scoringStart;

    const isCandidate = candidateScoring.candidate_for_ai;
    if (isCandidate) {
      metrics.candidatesFound++;
    }

    // Stage 3: Deep Scan (if candidate and within limits)
    let aiScan: AiScanResult | null = null;
    let deals: NormalizedDeal[] = [];

    const shouldDeepScan =
      config.deepScan.enabled &&
      isCandidate &&
      aiCallsUsed < config.deepScan.maxTotalAiCalls &&
      (config.deepScan.minCandidateScore === undefined ||
        candidateScoring.candidate_score >= config.deepScan.minCandidateScore);

    if (shouldDeepScan) {
      onProgress?.('AI Scan', pageNumber, pages.length);
      const aiStart = Date.now();
      aiScan = await runDeepScan(
        imageUrl,
        pageNumber,
        merchantName,
        ocr.text,
        config.extractor.model
      );
      metrics.aiDurationMs += Date.now() - aiStart;
      aiCallsUsed++;
      metrics.candidatesScanned++;

      if (aiScan.error) {
        errors.push({
          stage: 'deepScan',
          pageNumber,
          message: aiScan.error,
        });
      }

      // Normalize and validate deals
      const normalized = normalizeDeals(
        aiScan,
        runId,
        pageNumber,
        ocr.score_total,
        candidateScoring.candidate_score,
        candidateScoring.reason_codes,
        config.validation
      );

      deals = normalized.deals;
      metrics.dealsExtracted += normalized.deals.length;
      metrics.dealsRejected += normalized.rejected;

      for (const [reason, count] of Object.entries(normalized.rejectionReasons)) {
        metrics.rejectionReasons[reason] = (metrics.rejectionReasons[reason] || 0) + count;
      }
    }

    results.push({
      pageNumber,
      imageUrl,
      imageStoragePath,
      ocr,
      candidateScoring,
      isCandidate,
      aiScan,
      deals,
    });

    metrics.pagesProcessed++;
  }

  metrics.totalDurationMs = Date.now() - startTime;

  const hasErrors = errors.length > 0;
  const hasResults = metrics.dealsExtracted > 0 || metrics.pagesProcessed > 0;

  return {
    runId,
    merchantName,
    status: hasErrors && !hasResults ? 'failed' : hasErrors ? 'partial' : 'complete',
    config,
    pages: results,
    metrics,
    errors,
  };
}

// ============================================================================
// CONFIG HELPERS
// ============================================================================

/**
 * Create a config snapshot from UI settings (Diagnostics v2 format)
 */
export function createConfigFromDiagnosticsSettings(settings: {
  primaryOcrEngine: PrimaryOcrEngine;
  aiPageScanEnabled: boolean;
  aiModel: string;
  maxAiCalls: number;
  candidateScoringConfig?: CandidateScoringConfig;
}): FlyerExtractionConfig {
  const candidateConfig = settings.candidateScoringConfig || DEFAULT_CANDIDATE_SCORING_CONFIG;

  return {
    version: DEFAULT_EXTRACTION_CONFIG.version,
    ocr: {
      engine: settings.primaryOcrEngine,
    },
    candidateScoring: {
      threshold: candidateConfig.threshold,
      minOcrScore: candidateConfig.min_primary_ocr_score,
      signals: candidateConfig.signals,
    },
    deepScan: {
      enabled: settings.aiPageScanEnabled,
      maxCandidatesPerPage: 5,
      maxTotalAiCalls: settings.maxAiCalls,
      minCandidateScore: candidateConfig.threshold,
    },
    extractor: {
      model: settings.aiModel,
    },
    validation: DEFAULT_EXTRACTION_CONFIG.validation,
    diagnostics: DEFAULT_EXTRACTION_CONFIG.diagnostics,
  };
}

/**
 * Create a config snapshot from Batch Run config_json
 */
export function createConfigFromBatchRunSettings(batchConfig: {
  flyerExtraction?: {
    ocrEngine?: PrimaryOcrEngine;
    aiEnabled?: boolean;
    aiModel?: string;
    maxAiCalls?: number;
    candidateScoringProfileId?: string;
    candidateThreshold?: number;
    minOcrScore?: number;
  };
}): FlyerExtractionConfig {
  const fe = batchConfig.flyerExtraction || {};

  return {
    version: DEFAULT_EXTRACTION_CONFIG.version,
    ocr: {
      engine: fe.ocrEngine || 'tesseract',
    },
    candidateScoring: {
      profileId: fe.candidateScoringProfileId,
      threshold: fe.candidateThreshold ?? 12,
      minOcrScore: fe.minOcrScore ?? 25,
    },
    deepScan: {
      enabled: fe.aiEnabled ?? true,
      maxCandidatesPerPage: 5,
      maxTotalAiCalls: fe.maxAiCalls ?? 50,
    },
    extractor: {
      model: fe.aiModel || 'google/gemini-3-flash-preview',
    },
    validation: DEFAULT_EXTRACTION_CONFIG.validation,
    diagnostics: {
      storeEvidence: true,
      storeOcrText: true,
      storeAiRawOutput: true,
    },
  };
}
